import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { PosDeviceContext } from '../context/PosDeviceContext';
import { POS_HOME_ROLES } from '../config/roles';
import AppIcon from './icons/AppIcon';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [pinStep, setPinStep] = useState('enter');
  const [pickToken, setPickToken] = useState(null);
  const [storeChoices, setStoreChoices] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithPin, completePinStoreLogin } = useContext(AuthContext);
  const { enrolled: posDeviceEnrolled, loading: posDeviceLoading, device } = useContext(PosDeviceContext);
  const navigate = useNavigate();

  useEffect(() => {
    setError('');
    setPinStep('enter');
    setPickToken(null);
    setStoreChoices([]);
    setPin('');
  }, [posDeviceEnrolled]);

  const goAfterLogin = (user) => {
    if (posDeviceEnrolled && user && POS_HOME_ROLES.includes(user.role)) {
      navigate('/pos');
    } else {
      navigate('/');
    }
  };

  const resetPinFlow = () => {
    setPinStep('enter');
    setPickToken(null);
    setStoreChoices([]);
    setPin('');
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(username, password);
    if (result.success) goAfterLogin(result.user);
    else setError(result.error);
    setLoading(false);
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{4}$/.test(pin)) {
      setError('PIN must be exactly 4 digits');
      return;
    }
    setLoading(true);
    const result = await loginWithPin(pin);
    if (result.needsStoreSelection) {
      const list = result.stores || [];
      if (!list.length) {
        setError('No outlets assigned to your account. Ask an admin to assign stores.');
        setLoading(false);
        return;
      }
      setPinStep('stores');
      setPickToken(result.pickToken);
      setStoreChoices(list);
      setLoading(false);
      return;
    }
    if (result.success) {
      goAfterLogin(result.user);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handlePickStore = async (storeId) => {
    setError('');
    setLoading(true);
    const result = await completePinStoreLogin(pickToken, storeId);
    if (result.success) {
      goAfterLogin(result.user);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  if (posDeviceLoading) {
    return (
      <div className="login-page">
        <div className="login-page-grid" aria-hidden />
        <div className="login-page-glow" aria-hidden />
        <div className="login-shell">
          <div className="login-brand-block">
            <img src="/cakexpress-logo.png" alt="CakeXpress" className="login-brand-logo" />
          </div>
          <div className="login-card login-card--glass">
            <p className="login-lead" style={{ textAlign: 'center', margin: 0 }}>
              Checking terminal…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-page-grid" aria-hidden />
      <div className="login-page-glow" aria-hidden />

      <div className="login-shell">
        <div className="login-brand-block">
          <img src="/cakexpress-logo.png" alt="CakeXpress" className="login-brand-logo" />
          <p className="login-brand-tagline">
            {posDeviceEnrolled ? 'POS terminal' : 'Order hub · Franchise'}
          </p>
        </div>

        <div className="login-card login-card--glass">
          {posDeviceEnrolled ? (
            pinStep === 'enter' ? (
              <form className="login-form" onSubmit={handlePinSubmit}>
                <h1 className="login-headline">POS terminal</h1>
                <p className="login-lead">
                  {device?.store_name
                    ? `${device.store_name} — enter your 4-digit PIN to start.`
                    : 'Enter your 4-digit PIN to start.'}
                </p>
                <div className="login-field">
                  <label htmlFor="pin-code">4-digit PIN</label>
                  <input
                    id="pin-code"
                    type="password"
                    inputMode="numeric"
                    pattern="\d{4}"
                    maxLength={4}
                    className="login-input login-input--pin"
                    autoComplete="one-time-code"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    required
                  />
                </div>
                {error ? <div className="login-error">{error}</div> : null}
                <button type="submit" className="login-submit" disabled={loading}>
                  {loading ? 'Checking…' : 'Continue'}
                </button>
              </form>
            ) : (
              <div className="login-form login-store-pick">
                <h1 className="login-headline">Choose outlet</h1>
                <p className="login-lead">You have more than one store — pick where you are working now.</p>
                {error ? <div className="login-error">{error}</div> : null}
                <div className="login-store-grid" role="list">
                  {storeChoices.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      role="listitem"
                      className="login-store-tile"
                      disabled={loading}
                      onClick={() => handlePickStore(s.id)}
                    >
                      <span className="login-store-tile-name">{s.name}</span>
                      <span className="login-store-tile-code">{s.code}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className="login-back-pin"
                  disabled={loading}
                  onClick={() => {
                    resetPinFlow();
                    setError('');
                  }}
                >
                  ← Back to PIN
                </button>
              </div>
            )
          ) : (
            <form className="login-form" onSubmit={handleStaffSubmit}>
              <h1 className="login-headline">CakeXpress</h1>
              <p className="login-lead">Staff login for the operations console.</p>
              <div className="login-field">
                <label htmlFor="login-user">Username or email</label>
                <input
                  id="login-user"
                  type="text"
                  className="login-input"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </div>
              <div className="login-field">
                <label htmlFor="login-pass">Password</label>
                <div className="login-password-wrap">
                  <input
                    id="login-pass"
                    type={showPassword ? 'text' : 'password'}
                    className="login-input login-input--password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? <AppIcon name="eyeOff" size={16} /> : <AppIcon name="eye" size={16} />}
                  </button>
                </div>
              </div>
              {error ? <div className="login-error">{error}</div> : null}
              <button type="submit" className="login-submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Enter console'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
